import React, { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import { useAuth } from '../contexts/AuthContext'
import Sidebar from '../components/layout/Sidebar'
import OrganizationSetup from '../components/organization/OrganizationSetup'
import { StorageService } from '../services/storageService'
import { organizationService } from '../services/firestoreService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import SlidingToggle from '@/components/ui/SlidingToggle'
import { 
  Users, 
  Crown, 
  UserPlus, 
  Building2,
  Calendar,
  MoreHorizontal,
  Edit,
  Trash2,
  Upload,
  Image,
  Sun,
  Moon,
  Settings as SettingsIcon,
  CreditCard,
  BarChart3,
  Palette
} from 'lucide-react'
import { 
  hasOrganizationAtom,
  currentOrganizationAtom,
  currentMembershipAtom,
  organizationSetupAtom
} from '../store/organizationAtoms'
import ChartPreferencesSettings from '../components/settings/ChartPreferencesSettings'
import ColorPreferencesSettings from '../components/settings/ColorPreferencesSettings'
import { defaultChartPreferences } from '@shared/schemas/chartPreferences'
import type { ChartPreferences } from '@shared/schemas/chartPreferences'
import { PaletteManagementUI } from '@/components/settings/PaletteManagementUI'

const Organization: React.FC = () => {
  const { currentUser } = useAuth()
  const [hasOrganization] = useAtom(hasOrganizationAtom)
  const [currentOrganization] = useAtom(currentOrganizationAtom)
  const [currentMembership] = useAtom(currentMembershipAtom)
  const [setup] = useAtom(organizationSetupAtom)
  const [activeTab, setActiveTab] = useState('overview')
  const [lightLogo, setLightLogo] = useState<string | null>(null)
  const [darkLogo, setDarkLogo] = useState<string | null>(null)
  const [uploadingLightLogo, setUploadingLightLogo] = useState(false)
  const [uploadingDarkLogo, setUploadingDarkLogo] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [dragOverLight, setDragOverLight] = useState(false)
  const [dragOverDark, setDragOverDark] = useState(false)

  // Load existing logos and custom palettes when organization changes
  useEffect(() => {
    const loadOrganizationData = async () => {
      if (currentOrganization?.logos) {
        setLightLogo(currentOrganization.logos.light?.url || null)
        setDarkLogo(currentOrganization.logos.dark?.url || null)
      }
      
      if (currentOrganization?.id) {
        // Custom palettes are now loaded by the ColorPaletteSelector component
        // using the new colorPaletteService
      }
    }
    
    loadOrganizationData()
  }, [currentOrganization])

  // Show organization setup if user doesn't have one
  if (!hasOrganization || setup.step !== 'complete') {
    return (
      <Sidebar>
        <div className="space-y-8">
          <OrganizationSetup />
        </div>
      </Sidebar>
    )
  }

  // Use real organization data from atoms
  const organization = currentOrganization || {
    name: 'Unknown Organization',
    slug: 'unknown',
    description: '',
    website: '',
    logoUrl: '/logo/fireforge-logo.svg',
    memberCount: 1,
    createdAt: new Date(),
    plan: 'free',
    settings: {
      publicJoin: false,
      requireApproval: true,
      maxMembers: 5
    }
  }

  // Use current user's membership data
  const teamMembers = currentMembership ? [
    {
      id: currentMembership.id,
      email: currentMembership.email,
      displayName: currentMembership.displayName || currentUser?.displayName || 'Unknown User',
      photoURL: currentUser?.photoURL,
      role: currentMembership.role,
      joinedAt: currentMembership.joinedAt,
      lastActive: new Date(),
      status: currentMembership.status
    }
  ] : []

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-primary text-primary-foreground'
      case 'admin': return 'bg-orange-100 text-orange-800'
      case 'member': return 'bg-blue-100 text-blue-800'
      case 'viewer': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatLastActive = (date: Date) => {
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Active now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }

  const handleLogoUpload = async (file: File, type: 'light' | 'dark') => {
    if (!currentOrganization) {
      setLogoError('No organization found')
      return
    }

    const setUploading = type === 'light' ? setUploadingLightLogo : setUploadingDarkLogo
    const setLogo = type === 'light' ? setLightLogo : setDarkLogo

    try {
      setUploading(true)
      setLogoError(null)
      
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file)
      setLogo(previewUrl)
      
      // Upload to Firebase Storage
      const uploadResult = await StorageService.uploadOrganizationLogo(
        currentOrganization.id,
        file,
        type
      )
      
      // Update organization in Firestore with the new logo URL
      await organizationService.updateLogo(currentOrganization.id, type, {
        url: uploadResult.url,
        path: uploadResult.path
      })
      
      // Update the preview with the actual download URL
      setLogo(uploadResult.url)
      
      // Clean up the preview URL
      URL.revokeObjectURL(previewUrl)
      
      console.log(`${type} logo uploaded successfully:`, uploadResult.url)
      
    } catch (error) {
      console.error('Logo upload failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload logo. Please try again.'
      setLogoError(errorMessage)
      setLogo(null)
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'light' | 'dark') => {
    const file = event.target.files?.[0]
    if (file) {
      handleLogoUpload(file, type)
    }
  }

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent, type: 'light' | 'dark') => {
    event.preventDefault()
    event.stopPropagation()
    if (type === 'light') {
      setDragOverLight(true)
    } else {
      setDragOverDark(true)
    }
  }

  const handleDragLeave = (event: React.DragEvent, type: 'light' | 'dark') => {
    event.preventDefault()
    event.stopPropagation()
    if (type === 'light') {
      setDragOverLight(false)
    } else {
      setDragOverDark(false)
    }
  }

  const handleDrop = (event: React.DragEvent, type: 'light' | 'dark') => {
    event.preventDefault()
    event.stopPropagation()
    
    if (type === 'light') {
      setDragOverLight(false)
    } else {
      setDragOverDark(false)
    }

    const files = event.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        handleLogoUpload(file, type)
      } else {
        setLogoError('Please drop an image file')
      }
    }
  }

  return (
    <Sidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
                          <h1 className="text-3xl font-bold text-foreground">{organization.name}</h1>
            <p className="text-muted-foreground">{organization.description}</p>
            </div>
          </div>
          <Button>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Members
          </Button>
        </div>

      {/* SlidingToggle for Tab Navigation */}
      <div className="w-full flex justify-center py-2">
                  <SlidingToggle
            options={[
              { value: 'overview', label: 'Overview', icon: <Building2 className="h-4 w-4" /> },
              { value: 'members', label: 'Members', icon: <Users className="h-4 w-4" /> },
              { value: 'settings', label: 'Settings', icon: <SettingsIcon className="h-4 w-4" /> },
              { value: 'charts', label: 'Chart Display', icon: <BarChart3 className="h-4 w-4" /> },
              { value: 'colors', label: 'Colors', icon: <Palette className="h-4 w-4" /> },
              { value: 'billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> }
            ]}
          value={activeTab}
          onChange={setActiveTab}
          className="w-full max-w-xs sm:max-w-md lg:max-w-4xl" // Responsive width that grows on larger screens
        />
      </div>

      {/* Content Area based on activeTab */}
      <div className="mt-0 space-y-6"> {/* Removed top margin from here, handled by toggle container padding */}
        {/* Overview Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Organization Stats */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{organization.memberCount}</div>
                <p className="text-xs text-muted-foreground">
                  +1 from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Plan</CardTitle>
                <Crown className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{organization.plan}</div>
                <p className="text-xs text-muted-foreground">
                  {organization.settings.maxMembers} member limit
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Created</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {organization.createdAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {Math.floor((new Date().getTime() - organization.createdAt.getTime()) / (1000 * 60 * 60 * 24))} days ago
                </p>
              </CardContent>
            </Card>
          </div>
              </div>
        )}

        {/* Members Content */}
        {activeTab === 'members' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Manage your organization members and their roles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={member.photoURL || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(member.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{member.displayName}</span>
                          <Badge className={getRoleColor(member.role)}>
                            {member.role}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{member.email}</span>
                          <span>•</span>
                          <span>Joined {member.joinedAt.toLocaleDateString()}</span>
                          <span>•</span>
                          <span>{formatLastActive(member.lastActive)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.role !== 'owner' && (
                        <>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Settings Content */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>Manage your organization preferences and security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input id="orgName" defaultValue={organization.name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgSlug">URL Slug</Label>
                  <Input id="orgSlug" defaultValue={organization.slug} />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="orgDescription">Description</Label>
                <Input id="orgDescription" defaultValue={organization.description} />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="orgWebsite">Website</Label>
                <Input id="orgWebsite" defaultValue={organization.website} />
              </div>

                {/* Logo Upload Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Organization Logos</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload logos for light and dark themes. Drag and drop files or click to browse. Recommended size: 200x60px or similar aspect ratio.
                  </p>
                  
                  {logoError && (
                    <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                      <p className="text-sm text-destructive">{logoError}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Light Mode Logo */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Sun className="h-4 w-4 text-amber-500" />
                        <Label className="font-medium">Light Mode Logo</Label>
                      </div>
                      
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center bg-white dark:bg-gray-800/30 transition-colors cursor-pointer ${
                          dragOverLight 
                            ? 'border-primary bg-primary/5' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                        onDragOver={(e) => handleDragOver(e, 'light')}
                        onDragLeave={(e) => handleDragLeave(e, 'light')}
                        onDrop={(e) => handleDrop(e, 'light')}
                        onClick={() => document.getElementById('light-logo-upload')?.click()}
                      >
                        {lightLogo ? (
                          <div className="space-y-3">
                            <img 
                              src={lightLogo} 
                              alt="Light mode logo preview" 
                              className="max-h-16 mx-auto object-contain"
                            />
                            <p className="text-xs text-muted-foreground">Light mode logo preview</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <Image className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {dragOverLight ? 'Drop image here' : 'Drag & drop or click to upload light mode logo'}
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, 'light')}
                            className="hidden"
                            id="light-logo-upload"
                            disabled={uploadingLightLogo}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={uploadingLightLogo}
                            onClick={(e) => {
                              e.stopPropagation()
                              document.getElementById('light-logo-upload')?.click()
                            }}
                          >
                            {uploadingLightLogo ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Light Logo
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Dark Mode Logo */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                        <Label className="font-medium">Dark Mode Logo</Label>
                      </div>
                      
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center bg-gray-900 dark:bg-gray-800/30 transition-colors cursor-pointer ${
                          dragOverDark 
                            ? 'border-primary bg-primary/5' 
                            : 'border-gray-600 dark:border-gray-500 hover:border-gray-500 dark:hover:border-gray-400'
                        }`}
                        onDragOver={(e) => handleDragOver(e, 'dark')}
                        onDragLeave={(e) => handleDragLeave(e, 'dark')}
                        onDrop={(e) => handleDrop(e, 'dark')}
                        onClick={() => document.getElementById('dark-logo-upload')?.click()}
                      >
                        {darkLogo ? (
                          <div className="space-y-3">
                            <img 
                              src={darkLogo} 
                              alt="Dark mode logo preview" 
                              className="max-h-16 mx-auto object-contain"
                            />
                            <p className="text-xs text-gray-400 dark:text-gray-500">Dark mode logo preview</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <Image className="h-8 w-8 text-gray-500 dark:text-gray-400 mx-auto" />
                            <p className="text-sm text-gray-400 dark:text-gray-300">
                              {dragOverDark ? 'Drop image here' : 'Drag & drop or click to upload dark mode logo'}
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, 'dark')}
                            className="hidden"
                            id="dark-logo-upload"
                            disabled={uploadingDarkLogo}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={uploadingDarkLogo}
                            className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700 dark:bg-gray-700 dark:border-gray-500 dark:hover:bg-gray-600"
                            onClick={(e) => {
                              e.stopPropagation()
                              document.getElementById('dark-logo-upload')?.click()
                            }}
                          >
                            {uploadingDarkLogo ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Dark Logo
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground">
                    <p>• Supported formats: PNG, JPG, SVG</p>
                    <p>• Maximum file size: 5MB</p>
                    <p>• Recommended dimensions: 200x60px or similar aspect ratio</p>
                    <p>• Drag and drop files directly onto the upload areas</p>
                  </div>
                </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Member Settings</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">Public Join</span>
                      <p className="text-sm text-muted-foreground">Allow anyone to join with the invite link</p>
                    </div>
                    <Button variant="outline" size="sm">
                      {organization.settings.publicJoin ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">Require Approval</span>
                      <p className="text-sm text-muted-foreground">New members need admin approval</p>
                    </div>
                    <Button variant="outline" size="sm">
                      {organization.settings.requireApproval ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button>Save Changes</Button>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Chart Preferences Content */}
        {activeTab === 'charts' && (
          <div className="space-y-6">
            <ChartPreferencesSettings
              preferences={currentOrganization?.chartPreferences || defaultChartPreferences}
              onSave={async (preferences: ChartPreferences) => {
                if (!currentOrganization?.id) return;
                
                try {
                  await organizationService.update(currentOrganization.id, {
                    chartPreferences: preferences,
                  });
                  
                  // Update local state would require a refresh mechanism
                  // For now, we could show a success message
                  console.log('Chart preferences updated successfully');
                } catch (error) {
                  console.error('Error updating chart preferences:', error);
                  throw error;
                }
              }}
              title="Organization Chart Preferences"
              description="Set default chart preferences for all members of your organization. Members can override these settings in their personal preferences."
            />
          </div>
        )}

        {/* Color Palettes Tab (Replaces previous Colors tab content) */}
        {activeTab === 'colors' && (
          <PaletteManagementUI 
            userId={currentUser?.uid || ''} 
            organizationId={currentOrganization?.id || ''} 
            currentUserChartPreferences={currentOrganization?.chartPreferences || defaultChartPreferences} 
            onUserDefaultPaletteChange={async (paletteId: string) => {
              console.log('🎨 Changing user default palette to:', paletteId)
              // This would be implemented similar to Profile.tsx
            }}
          />
        )}

        {/* Billing Content */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Billing & Subscription</CardTitle>
              <CardDescription>Manage your organization's subscription and billing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Current Plan: {organization.plan}</h3>
                  <p className="text-sm text-muted-foreground">
                    Up to {organization.settings.maxMembers} members
                  </p>
                </div>
                <Button variant="outline">Upgrade Plan</Button>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Usage</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Members</span>
                    <span>{organization.memberCount} / {organization.settings.maxMembers}</span>
                  </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${(organization.memberCount / organization.settings.maxMembers) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}
      </div>
    </div>
    </Sidebar>
  )
}

export default Organization 